import "./style.css";
import "./portal.css";
import { AppController } from "./app/AppController";
import { waitForAccount } from "./auth/AccountGate";
import { resolveWorldRendererMode } from "./game/presentation/WorldPresentation";
import { GameNetworkClient } from "./network/GameNetworkClient";

declare global {
  interface Window {
    __bastionBootErrors?: string[];
  }
}

window.__bastionBootErrors = [];
window.addEventListener("error", (event) => {
  window.__bastionBootErrors?.push(event.error?.stack ?? event.message);
});
window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason;
  window.__bastionBootErrors?.push(
    reason instanceof Error ? (reason.stack ?? reason.message) : String(reason),
  );
});

const GAME_QUERY_KEYS = [
  "play",
  "room",
  "autocreate",
  "network",
  "renderer",
  "guide",
  "seed",
  "name",
  "ready",
  "quality",
  "codex",
] as const;

if (isGameExperience(window.location.search)) {
  document.body.dataset.siteMode = "game";
  requiredElement<HTMLElement>("site-home").classList.add("hidden");
  void bootstrap(new GameNetworkClient());
} else {
  document.body.dataset.siteMode = "portal";
  if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    document.documentElement.style.scrollBehavior = "smooth";
  }
}

async function bootstrap(network: GameNetworkClient): Promise<void> {
  await waitForAccount(network);
  const renderer = resolveWorldRendererMode(window.location.search);
  if (renderer === "babylon") {
    const { BabylonGameView } = await import(
      "./game/babylon/BabylonGameView"
    );
    const view = new BabylonGameView(
      network,
      requiredElement<HTMLElement>("game"),
    );
    await view.start();
  } else {
    const [{ default: Phaser }, { GameScene }] = await Promise.all([
      import("phaser"),
      import("./game/scenes/GameScene"),
    ]);
    const lowQuality =
      new URLSearchParams(window.location.search).get("quality") === "low";
    new Phaser.Game({
      type: Phaser.AUTO,
      parent: "game",
      backgroundColor: "#071014",
      width: window.innerWidth,
      height: window.innerHeight,
      antialias: !lowQuality,
      antialiasGL: !lowQuality,
      desynchronized: lowQuality,
      render: {
        antialias: !lowQuality,
        roundPixels: lowQuality,
        powerPreference: "high-performance",
      },
      scale: {
        mode: Phaser.Scale.RESIZE,
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
      input: {
        activePointers: 3,
        gamepad: true,
      },
      scene: [new GameScene(network)],
    });
  }
  new AppController(network);
}

function isGameExperience(search: string): boolean {
  const parameters = new URLSearchParams(search);
  return GAME_QUERY_KEYS.some((key) => parameters.has(key));
}

function requiredElement<T extends HTMLElement>(id: string): T {
  const value = document.getElementById(id);
  if (!(value instanceof HTMLElement)) {
    throw new Error(`Missing required element #${id}`);
  }
  return value as T;
}
